import React from "react";
import { injectStyles } from "./inject-styles";

/* ------------------------------------------------------------------ */
/*  Tabs — ARIA tab group with keyboard navigation                    */
/* ------------------------------------------------------------------ */

const css = /* css */ `
  .sf-tabs__list {
    display: flex;
    gap: 0;
    border-bottom: 1px solid var(--sf-border);
    font-family: var(--sf-font-sans);
  }

  .sf-tabs__trigger {
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: var(--sf-space-1);
    padding: var(--sf-space-2) var(--sf-space-3);
    font-size: var(--sf-text-sm);
    font-weight: var(--sf-weight-medium);
    color: var(--sf-text-muted);
    background: none;
    border: none;
    cursor: pointer;
    white-space: nowrap;
    transition: color var(--sf-transition-fast);
  }

  .sf-tabs__trigger:hover {
    color: var(--sf-text);
  }

  .sf-tabs__trigger:focus-visible {
    outline: var(--sf-ring-width) solid var(--sf-ring-color);
    outline-offset: calc(-1 * var(--sf-ring-offset));
    border-radius: var(--sf-radius-sm);
  }

  .sf-tabs__trigger[aria-selected="true"] {
    color: var(--sf-primary);
  }

  .sf-tabs__trigger[aria-selected="true"]::after {
    content: "";
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 2px;
    background: var(--sf-primary);
    border-radius: 1px 1px 0 0;
  }

  .sf-tabs__panel {
    padding: var(--sf-space-4) 0;
  }

  .sf-tabs__panel:focus-visible {
    outline: var(--sf-ring-width) solid var(--sf-ring-color);
    outline-offset: var(--sf-ring-offset);
    border-radius: var(--sf-radius-sm);
  }
`;

/* -- Context ------------------------------------------------------- */

interface TabsContextValue {
  value: string;
  onValueChange: (value: string) => void;
  baseId: string;
}

const TabsContext = React.createContext<TabsContextValue | null>(null);

function useTabsContext(): TabsContextValue {
  const ctx = React.useContext(TabsContext);
  if (!ctx) throw new Error("Tabs compound components must be used within <Tabs>");
  return ctx;
}

/* -- Tabs (root) --------------------------------------------------- */

export interface TabsProps {
  /** Currently active tab value. */
  value: string;
  /** Callback when a tab is selected. */
  onValueChange: (value: string) => void;
  children: React.ReactNode;
  className?: string;
}

function Tabs({ value, onValueChange, children, className }: TabsProps) {
  injectStyles("sf-tabs", css);
  const baseId = React.useId();
  return (
    <TabsContext.Provider value={{ value, onValueChange, baseId }}>
      <div className={className}>{children}</div>
    </TabsContext.Provider>
  );
}
Tabs.displayName = "Tabs";

/* -- TabsList ------------------------------------------------------ */

export interface TabsListProps extends React.HTMLAttributes<HTMLDivElement> {}

const TabsList = React.forwardRef<HTMLDivElement, TabsListProps>(
  ({ children, className, ...rest }, ref) => {
    const classes = ["sf-tabs__list", className ?? ""]
      .filter(Boolean)
      .join(" ");

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      const triggers = Array.from(
        (e.currentTarget as HTMLElement).querySelectorAll<HTMLButtonElement>(
          '[role="tab"]',
        ),
      );
      const currentIndex = triggers.findIndex(
        (t) => t === document.activeElement,
      );
      if (currentIndex === -1) return;

      let nextIndex = currentIndex;
      if (e.key === "ArrowRight") {
        nextIndex = (currentIndex + 1) % triggers.length;
      } else if (e.key === "ArrowLeft") {
        nextIndex = (currentIndex - 1 + triggers.length) % triggers.length;
      } else if (e.key === "Home") {
        nextIndex = 0;
      } else if (e.key === "End") {
        nextIndex = triggers.length - 1;
      } else {
        return;
      }

      e.preventDefault();
      triggers[nextIndex].focus();
      triggers[nextIndex].click();
    };

    return (
      <div
        ref={ref}
        role="tablist"
        className={classes}
        onKeyDown={handleKeyDown}
        {...rest}
      >
        {children}
      </div>
    );
  },
);
TabsList.displayName = "TabsList";

/* -- TabsTrigger --------------------------------------------------- */

export interface TabsTriggerProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** Value identifying this tab. Must match a TabsPanel value. */
  value: string;
}

const TabsTrigger = React.forwardRef<HTMLButtonElement, TabsTriggerProps>(
  ({ value, children, className, ...rest }, ref) => {
    const { value: selected, onValueChange, baseId } = useTabsContext();
    const isSelected = value === selected;
    const classes = ["sf-tabs__trigger", className ?? ""]
      .filter(Boolean)
      .join(" ");

    return (
      <button
        ref={ref}
        role="tab"
        id={`${baseId}-tab-${value}`}
        aria-selected={isSelected}
        aria-controls={`${baseId}-panel-${value}`}
        tabIndex={isSelected ? 0 : -1}
        className={classes}
        onClick={() => onValueChange(value)}
        {...rest}
      >
        {children}
      </button>
    );
  },
);
TabsTrigger.displayName = "TabsTrigger";

/* -- TabsPanel ----------------------------------------------------- */

export interface TabsPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Value identifying this panel. Must match a TabsTrigger value. */
  value: string;
  /** Keep panel mounted when inactive. Default false. */
  forceMount?: boolean;
}

const TabsPanel = React.forwardRef<HTMLDivElement, TabsPanelProps>(
  ({ value, forceMount = false, children, className, ...rest }, ref) => {
    const { value: selected, baseId } = useTabsContext();
    const isActive = value === selected;

    if (!isActive && !forceMount) return null;

    const classes = ["sf-tabs__panel", className ?? ""]
      .filter(Boolean)
      .join(" ");

    return (
      <div
        ref={ref}
        role="tabpanel"
        id={`${baseId}-panel-${value}`}
        aria-labelledby={`${baseId}-tab-${value}`}
        tabIndex={0}
        hidden={!isActive}
        className={classes}
        {...rest}
      >
        {children}
      </div>
    );
  },
);
TabsPanel.displayName = "TabsPanel";

export { Tabs, TabsList, TabsTrigger, TabsPanel };
